import { QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { Toaster } from "react-hot-toast";
import { BrowserRouter } from "react-router-dom";

import App from "./App";
import { captureAuthResponseBeforeTelemetry } from "./auth/previewRelay";
import AuthGate from "./components/AuthGate";
import { initSentry } from "./lib/sentry";
import { installStaleChunkRecovery } from "./lib/staleChunkRecovery";
import { createAppQueryClient } from "./queryClient";
import "./base.css";
import "./design-system/styles.css";

installStaleChunkRecovery(window);

let callbackUrl: string | null = null;
let callbackError: string | null = null;
try {
  callbackUrl = captureAuthResponseBeforeTelemetry(window.location, (path) =>
    window.history.replaceState(null, "", path),
  );
} catch (error: unknown) {
  callbackError =
    error instanceof Error ? error.message : "OAuth response is invalid";
}

// OAuth material is removed from the visible URL before monitoring starts.
// Empty runtime DSN is the supported local-development posture.
initSentry("fin-lake-front");

// This outer client owns only the sign-in mutation. Authenticated domain data
// lives in the principal-keyed nested client created by AuthGate.
const queryClient = createAppQueryClient();

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("#root element is missing from index.html");

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthGate callbackUrl={callbackUrl} callbackError={callbackError}>
          <App />
        </AuthGate>
      </BrowserRouter>
      <Toaster position="top-right" toastOptions={{ duration: 5000 }} />
    </QueryClientProvider>
  </StrictMode>,
);
